import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SlotDto } from '@motoschool/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { DayStrip } from '../components/DayStrip';
import { SlotCard } from '../components/SlotCard';
import { SlotDetailSheet } from '../components/SlotDetailSheet';
import { CreateSlotSheet } from '../components/CreateSlotSheet';
import { Button, EmptyState, ErrorNotice, PageLoader, SectionTitle, Select } from '../components/ui';
import { useTeachers } from '../lib/useTeachers';
import { dayKeyOf, humanDayLabel, toDateInputValue } from '../lib/format';

/** Горизонт планирования в ленте дней. */
const VISIBLE_DAYS = 21;

interface SlotsResponse {
  items: SlotDto[];
  rangeStart: string;
  rangeEnd: string;
}

export function SchedulePage(): React.JSX.Element {
  const { hasRole, timeZone } = useAuth();
  const isStaff = hasRole('TEACHER', 'ADMIN');
  const isAdmin = hasRole('ADMIN');

  // Инструктор и так видит только своё расписание, фильтр нужен администратору
  const [teacherFilter, setTeacherFilter] = useState('');
  const { data: teachers } = useTeachers(isAdmin);

  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [openSlot, setOpenSlot] = useState<SlotDto | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const days = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: VISIBLE_DAYS }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, []);

  const rangeStart = days[0] ?? new Date();
  const rangeEnd = new Date((days.at(-1) ?? new Date()).getTime() + 86_400_000);

  const { data, isLoading, error } = useQuery({
    queryKey: ['slots', rangeStart.toISOString(), rangeEnd.toISOString(), teacherFilter],
    queryFn: () =>
      api.get<SlotsResponse>('/slots', {
        from: rangeStart.toISOString(),
        to: rangeEnd.toISOString(),
        teacherId: teacherFilter || undefined,
      }),
  });

  const slots = data?.items ?? [];
  const selectedKey = dayKeyOf(selectedDay.toISOString(), timeZone);

  const countsByDay = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const slot of slots) {
      const key = dayKeyOf(slot.startsAt, timeZone);
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [slots, timeZone]);

  const daySlots = useMemo(
    () => slots.filter((slot) => dayKeyOf(slot.startsAt, timeZone) === selectedKey),
    [slots, selectedKey, timeZone],
  );

  // Ученику полезнее видеть сразу свои ближайшие занятия, а не пустой день.
  const myUpcoming = useMemo(
    () =>
      isStaff
        ? []
        : slots
            .filter((slot) => slot.student !== null && new Date(slot.startsAt).getTime() > Date.now())
            .slice(0, 3),
    [slots, isStaff],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Расписание</h1>
          <p className="text-sm text-muted">
            {isStaff ? 'Записи учеников и занятое время' : 'Ваши занятия'}
          </p>
        </div>
        {isStaff ? (
          <Button onClick={() => setIsCreating(true)}>
            <span aria-hidden>＋</span> Занять
          </Button>
        ) : (
          <Link to="/book">
            <Button>
              <span aria-hidden>＋</span> Записаться
            </Button>
          </Link>
        )}
      </div>

      {!isStaff && myUpcoming.length > 0 && (
        <section>
          <SectionTitle>Ближайшие занятия</SectionTitle>
          <div className="space-y-2">
            {myUpcoming.map((slot) => (
              <SlotCard
                key={slot.id}
                slot={slot}
                timeZone={timeZone}
                onClick={() => setOpenSlot(slot)}
              />
            ))}
          </div>
        </section>
      )}

      {isAdmin && (
        <Select
          value={teacherFilter}
          onChange={(event) => setTeacherFilter(event.target.value)}
          aria-label="Расписание инструктора"
        >
          <option value="">Все инструкторы</option>
          {teachers?.items.map((teacher) => (
            <option key={teacher.id} value={teacher.id}>
              {teacher.name}
            </option>
          ))}
        </Select>
      )}

      <DayStrip
        days={days}
        selectedKey={selectedKey}
        timeZone={timeZone}
        counts={countsByDay}
        onSelect={setSelectedDay}
      />

      {error && <ErrorNotice message="Не удалось загрузить расписание" />}

      {isLoading ? (
        <PageLoader />
      ) : (
        <section>
          <SectionTitle>{humanDayLabel(selectedDay.toISOString(), timeZone)}</SectionTitle>

          {daySlots.length === 0 ? (
            <EmptyState
              title="На этот день занятий нет"
              description={
                isStaff
                  ? 'Ученики записываются сами по вашим часам работы. Здесь можно закрыть время под занятие, о котором договорились не через сайт.'
                  : 'В этот день у вас занятий нет.'
              }
              action={
                isStaff ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button variant="secondary" onClick={() => setIsCreating(true)}>
                      Занять время
                    </Button>
                    {/* Пустой день чаще всего значит «я в этот день не работаю» */}
                    <Link to="/working-hours">
                      <Button variant="secondary">Часы работы</Button>
                    </Link>
                  </div>
                ) : (
                  <Link to="/book">
                    <Button>Записаться</Button>
                  </Link>
                )
              }
            />
          ) : (
            <div className="space-y-2">
              {daySlots.map((slot) => (
                <SlotCard
                  key={slot.id}
                  slot={slot}
                  timeZone={timeZone}
                  hideStudent={!isStaff}
                  onClick={() => setOpenSlot(slot)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <SlotDetailSheet slot={openSlot} onClose={() => setOpenSlot(null)} />

      {isStaff && (
        <CreateSlotSheet
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          defaultDate={toDateInputValue(selectedDay, timeZone)}
          timeZone={timeZone}
          defaultTeacherId={teacherFilter || null}
        />
      )}
    </div>
  );
}
